import { Typography } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Form } from "formik";
import moment from 'moment';
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { getMaxAppointmentsDays } from "../../../../../actions/appointments";
import {
  getFormProfessionals,
  getFormProfessionalSpecialties,
} from "../../../../../actions/forms";
import { convertDateTimeToNet } from "../../../../../utils/datetimeUtils";
import CustomDialog from "../../../../commons/dialog/customDialog";
import FormikActions from "../../../../commons/formik/formikActions";
import FormikAutocomplete from "../../../../commons/formik/formikAutocomplete";
import FormikCheckbox from "../../../../commons/formik/formikCheckbox";
import FormikDateTimePicker from "../../../../commons/formik/formikDateTimePicker";
import FormikForm from "../../../../commons/formik/formikForm";
import FormikTextField from "../../../../commons/formik/formikTextField";
import FormikTextFieldSpeech from "../../../../commons/formik/formikTextFieldSpeech";
import CircularLoading from "../../../../commons/loadingData/circularLoading";
import {
  StyledAppointments,
  StyledCheckbox,
  StyledInput,
  StyledProfessional,
} from "../../../../styled/appointments";

const validation = (appointmentsSession, enablePastDates) =>
  Yup.object({
    fechaHora: Yup.date()
      .required("Campo obligatorio")
      .when([], {
        is: () => !enablePastDates,
        then: (schema) =>
          schema.min(
            new Date(Date.now()),
            "La fecha del turno no puede ser menor a la fecha actual",
          ),
        otherwise: (schema) => schema, // Mantener el esquema sin restricciones adicionales
      })
      .typeError("Fecha inválida")
      .test({
        name: "fechaHora",
        message: "Ya tiene un turno cargado para esta fecha y hora",
        test: function (value) {
          if (appointmentsSession) {
            const sameAppointment = appointmentsSession.find((a) => {
              return a.fechaHora.getTime() === value.getTime();
            });
            return sameAppointment ? false : true;
          } else {
            return true;
          }
        },
      }),
    especialidad: Yup.string().required("Campo obligatorio"),
    profesionalApellidoNombre: Yup.string().required("Campo obligatorio"),
    matricula: Yup.string().required("Campo obligatorio"),
    observaciones: Yup.string().max(200, "Máximo de 200 caracteres.").nullable(),
  });

const defaultAppointmentModel = {
  fechaHora: null,
  especialidad: null,
  profesional: null,
  profesionalApellidoNombre: null,
  matricula: "",
  observaciones: "",
  traslado: false,
  asistio: false,
};

const AddAppointment = ({
  open,
  handleOpenConfirmDialog,
  handleSubmit,
  artFields,
  appointmentModel = defaultAppointmentModel,
  especialidadesProfesional,
  session,
  appointmetsSession,
  appointmentDetail,
  artId,
  errorProfessionalsDuplicated,
  openAppointmentCollection,
  openInCalendar,
  enablePastDates = true,
  preloadedProfessional,
  preloadedMnMd,
  preloadedSpecialty
}) => {
  const dispatch = useDispatch();
  const professionals = useSelector((state) => state.forms.professionals);
  const professionalSpecialties = useSelector((state) => state.forms.professionalSpecialties);
  const postAppointment = useSelector((state) => state.appointments.postAppointment);
  const maxDaysWarning = useSelector((state) => state.appointments.maxDaysWarning);

  const isLoading =
    professionals.isFetching ||
    professionalSpecialties.isFetching ||
    postAppointment.isFetching ||
    maxDaysWarning.isFetching;

  useEffect(() => {
    if (open && professionals.data.length === 0) {
      dispatch(getFormProfessionals(artId));
    }

    if (open) {
      dispatch(getMaxAppointmentsDays(artId));
    }
    if (
      open &&
      !especialidadesProfesional &&
      professionalSpecialties.data.length === 0
    ) {
      dispatch(getFormProfessionalSpecialties(artId));
    }
  }, [open]);
  return (
    <CustomDialog
      open={open}
      onClose={handleOpenConfirmDialog}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {appointmentModel.idTurnoSiniestro ? "Editar Turno" : "Agregar Turno"}
      </DialogTitle>
      {isLoading ? (
        <StyledAppointments.LoadingContainer>
          <CircularLoading />
        </StyledAppointments.LoadingContainer>
      ) : (
        <FormikForm
          artFields={artFields}
          initialValues={{
            ...appointmentModel,
            fechaHora: appointmentModel.fechaHora
              ? new Date(appointmentModel.fechaHora) // Convertir si es un string
              : null,
            profesionalApellidoNombre: preloadedProfessional || appointmentModel.profesionalApellidoNombre,
            matricula: preloadedMnMd || appointmentModel.matricula,
            especialidad: preloadedSpecialty || appointmentModel.especialidad,
          }}
          validationSchema={() =>
            validation(appointmetsSession, enablePastDates)
          }
          onSubmit={(values) => {
            const profesional = professionals.data.find(
              (p) => p.nombre === values.profesionalApellidoNombre,
            );
            let especialidad = professionalSpecialties.data.find(
              (e) => e.id === values.especialidad,
            );
            if (!especialidad) {
              especialidad = especialidadesProfesional.find(
                (e) => e.id === values.especialidad,
              );
            }
            values.profesional = profesional ? profesional.id : null;
            const createProfessional = !profesional ? true : false;
            handleSubmit(
              {
                ...values,
                especialidadNombre: especialidad.nombre,
                concurrio:
                  enablePastDates && values.fechaHora < new Date()
                    ? values.asistio
                      ? 1
                      : 2
                    : null,
                fromDB: appointmentModel.fromDB,
              },
              createProfessional,
              convertDateTimeToNet(values.fechaHora) ===
              convertDateTimeToNet(appointmentModel.fechaHora),
            );
          }}>
          {({ setFieldValue, values }) => {
            useEffect(() => {
              if (preloadedProfessional) {
                const profesional = professionals.data.find(
                  (p) => p.nombre === preloadedProfessional
                );
                if (profesional) {
                  if (!preloadedMnMd) setFieldValue("matricula", profesional.matricula);
                  if (!preloadedSpecialty) setFieldValue("especialidad", profesional.especialidad);
                }
              }
            }, [preloadedProfessional, professionals.data, setFieldValue]);
            return (
              <Form>

                <DialogContent>
                  <StyledAppointments.RowContainer>
                    <FormikDateTimePicker
                      name="fechaHora"
                      label="Fecha Hora Turno *"
                      variant="standard"
                      sx={StyledInput}
                      autoComplete="off"
                      disablePast={
                        !enablePastDates || !!appointmentModel.idTurnoSiniestro
                      }
                    />
                    <FormikCheckbox
                      name="traslado"
                      label="Traslado"
                      labelPlacement="end"
                      sx={StyledCheckbox}
                      disabled={appointmentModel.traslado}
                    />
                    {enablePastDates &&
                      values.fechaHora &&
                      values.fechaHora < new Date() && (
                        <FormikCheckbox
                          name="asistio"
                          label="¿Asistió?"
                          labelPlacement="end"
                          sx={StyledCheckbox}
                        />
                      )}
                  </StyledAppointments.RowContainer>
                  {maxDaysWarning.data !== 0 && moment(values.fechaHora).diff(moment(), 'days') >= maxDaysWarning.data && (
                    <Typography color="error">
                      ADVERTENCIA: Está cargando un turno con más de <strong>{maxDaysWarning.data} días</strong> de anticipación
                    </Typography>
                  )}
                  {convertDateTimeToNet(values.fechaHora) !==
                    convertDateTimeToNet(appointmentModel.fechaHora) &&
                    appointmentDetail ? (
                    <Typography color="error">
                      {
                        "IMPORTANTE: Al modificar la Fecha Hora de este Turno, se anulará el mismo, y se creará uno nuevo con los datos ingresados. Si posee traslado en el siguiente paso deberá ingresarlo."
                      }
                    </Typography>
                  ) : (
                    enablePastDates &&
                    values.fechaHora &&
                    values.fechaHora < new Date() && (
                      <Typography color="error">
                        {
                          "IMPORTANTE: Solo se permiten turnos individuales para fechas pasadas."
                        }
                      </Typography>
                    )
                  )}
                  <StyledAppointments.RowContainer>
                    <FormikAutocomplete
                      name="profesionalApellidoNombre"
                      label="Profesional *"
                      variant="standard"
                      options={professionals.data}
                      getOptions={() => dispatch(getFormProfessionals(artId))}
                      callBack={(value) => {
                        if (value) {
                          setFieldValue("matricula", value.matricula);
                          setFieldValue("especialidad", value.especialidad);
                        }
                      }}
                      sx={{ ...StyledProfessional }}
                      freeSolo={true}
                      returnProp="nombre"
                      helperText="Nuevo Profesional"
                      renderOption={(option) =>
                        `${option.matricula} - ${option.nombre}`
                      }
                      filterOptions={(option) =>
                        `${option.matricula} - ${option.nombre}`
                      }
                      disabled={session}
                    />
                    <FormikTextField
                      name="matricula"
                      label="Matrícula *"
                      variant="standard"
                      type="text"
                      sx={StyledInput}
                      disabled={session}
                    />
                  </StyledAppointments.RowContainer>
                  <StyledAppointments.RowContainer>
                    <FormikAutocomplete
                      name="especialidad"
                      variant="standard"
                      label="Especialidad *"
                      options={
                        especialidadesProfesional
                          ? especialidadesProfesional
                          : professionalSpecialties.data
                      }
                      sx={StyledInput}
                      disabled={session}
                    />
                  </StyledAppointments.RowContainer>
                  <StyledAppointments.RowContainer>
                    <FormikTextFieldSpeech
                      margin="dense"
                      id="observaciones"
                      name="observaciones"
                      variant="outlined"
                      label="Observaciones"
                      type="text"
                      fullWidth
                      multiline
                      sx={StyledInput}
                    />
                  </StyledAppointments.RowContainer>
                  {errorProfessionalsDuplicated && (
                    <Typography color="error">
                      Ya existe un profesional con este nombre.
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <FormikActions
                    handleCancel={handleOpenConfirmDialog}
                    hasThirdButton={
                      enablePastDates
                        ? openInCalendar &&
                        (!values.fechaHora || !(values.fechaHora < new Date()))
                        : openInCalendar
                    }
                    thirdButtonText="Ingresar sesiones"
                    handleThirdButton={openAppointmentCollection}
                  />
                </DialogActions>
              </Form>
            )
          }}
        </FormikForm>
      )}
    </CustomDialog>
  );
};

export default AddAppointment;
